import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useValidators } from "@/shared/utils/validate";
import { ErrorDialogContext, /**errorHandledAsync,*/ errorHandledSync, } from "@/shared/context/error-context";
import { SignUpService } from "@/features/user/services/sign-up-service";
import { useSignUpStates } from "@/features/user/hooks/use-sign-up-states";
import { MoneyLinkWarning } from "@/shared/utils/custom-exception";
import { utilities } from "@/shared/utils/utility";
import { EndpointPathNames } from "@/shared/config/endpoint-path";
import { LoadingModalContext } from "@/shared/components/loading/loading-context";
import { useLoginContext } from "@/features/auth/contexts/login-context";

export const useSignUp = () => {
  const signUpService = new SignUpService();
  const navigate = useNavigate();

  const {
    email,
    setEmail,
  } = useLoginContext();

  const {
    emailError, // メールアドレスのエラーメッセージ
    setEmailError,
    isActiveSendMailButton, // メール送信ボタンの有効フラグ
    setIsActiveSendMailButton,
    isShowErrorDialog, // エラーダイアログ表示フラグ情報
    setIsShowErrorDialog,
  } = useSignUpStates();

  // グローバルエラーハンドリング
  const { setError } = useContext(ErrorDialogContext);

  // ローディング用コンテキスト
  const loadingCtx = useContext(LoadingModalContext);

  // バリデーション関数の呼び出し
  const { validatesForm } = utilities();
  const { mailAddressValidator } = useValidators();

  const [newEmail, setNewEmail] = useState(email);

  // 新規登録時の入力メールアドレスの変更
  const onChangeMailAddress = (newValue: string) => {
    setEmail(newValue);
    setNewEmail(newValue);
  };

  // 初回認証時の新規入力メールアドレスの変更
  const onChangeNewMailAddress = (newValue: string) => {
    setNewEmail(newValue);
  };

  // ボタン押下イベント ======================================
  // 「メールを送信」押下時のコールバック
  // 入力したメールアドレスは認証用メールのためstateを利用して状態管理を行う
  // コンバート後初回認証時は現利用者IDがそのまま利用されるとも限らないため，oldEmailとして渡す．新規登録時は認証用と同値．
  const onClickSendMail = async (
    authenticateType: number, // 0:新規利用者登録、1: コンバート後初回認証
    isChangeMailChecked: boolean = true, // メールアドレス変更チェックボックスの状態
  ) => {
    loadingCtx.setIsLoading(true);

    // 認証用メールアドレスと認証ルートをlocalStorageに保存
    localStorage.setItem("signUpEmail", newEmail);
    localStorage.setItem("signUpAuthType", String(authenticateType));
    try {
      const hasValidationError = validatesForm([mailAddressValidator], newEmail, setEmailError);

      if (!hasValidationError) {
        // メールアドレス変更チェックなしの場合はインラインエラーが表示されないためダイアログで表示
        if (!isChangeMailChecked) {
          setError(new MoneyLinkWarning({
            title: "メールアドレスを変更してください",
            message: "現在ご登録のメールアドレスは対応していない形式のため、ご利用いただけません。\n「メールアドレスを変更する」にチェックを入れて、ご利用可能なメールアドレスに変更してください。",
          }));
          setEmailError("");
        }
        return;
      }

      await signUpService.register({
        old_owner_id: email,
        new_owner_id: newEmail,
        authenticate_type: authenticateType, // 0:新規利用者登録, 1: コンバート後初回認証
      });

      // 仮登録完了画面へ遷移
      navigateSignUpDone();
    } catch (error) {
      console.log(error);
      throw error;
    } finally {
      loadingCtx.setIsLoading(false);
    }
  };

  // 仮登録完了画面へ遷移
  const navigateSignUpDone = () => {
    // ブラウザバックで戻れないよう履歴を置換する
    navigate(EndpointPathNames.SIGN_UP_DONE, { replace: true });
  };

  // エラーダイアログを閉じる
  const closeErrorDialog = () => {
    setIsShowErrorDialog({
      isError: false,
      errorMessage: "",
    });
  };

  return {
    email,
    setEmail,
    newEmail,
    setNewEmail,
    emailError,
    setEmailError,
    isActiveSendMailButton,
    setIsActiveSendMailButton,
    isShowErrorDialog,
    setIsShowErrorDialog,
    onChangeMailAddress: errorHandledSync(onChangeMailAddress, setError),
    onChangeNewMailAddress: errorHandledSync(onChangeNewMailAddress, setError),
    onClickSendMail: errorHandledSync(onClickSendMail, setError),
    closeErrorDialog: errorHandledSync(closeErrorDialog, setError),
    //changeSendMailButtonState: errorHandledSync(changeSendMailButtonState, setError),
    // 登録でお困りの方はこちらリンク
    // openRegisterFAQ: errorHandledSync(openRegisterFAQ, setError),
    // メールが届かない場合リンク
    // openMailFAQ: errorHandledSync(openMailFAQ, setError),
  };
};
